import { request } from '../lib/api'
import type {
  ChangeRequestDetail,
  ChangeRequestList,
  Page,
  Project,
  RequestStatus,
  Snapshot,
  SnapshotSummary,
  SystemInfo,
  Task,
  User,
  Viewport,
} from '../lib/types'

export interface ProjectInput {
  name: string
  website_url: string
  github_repo: string | null
  github_branch: string | null
  description: string | null
  allowed_hosts?: string[]
}

export type TaskUpdate = Partial<Pick<Task, 'suggested_action' | 'required_changes' | 'acceptance_criteria' | 'restrictions'>> & {
  title?: string
  priority?: string
  category?: string
  assignee_id?: number | null
}

const qs = (params: Record<string, string | number | null | undefined>) => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => value !== null && value !== undefined && value !== '' && search.set(key, String(value)))
  const text = search.toString()
  return text ? `?${text}` : ''
}

export const staffApi = {
  me: () => request<User>('/api/auth/me'),
  login: (email: string, password: string) => request<User>('/api/auth/login', { json: { email, password } }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (current_password: string, new_password: string) =>
    request<User>('/api/auth/password', { json: { current_password, new_password } }),
  system: () => request<SystemInfo>('/api/system'),

  users: () => request<User[]>('/api/users'),
  createUser: (body: { email: string; name: string; password: string; role: string }) =>
    request<User>('/api/users', { json: body }),
  updateUser: (id: number, body: Partial<{ name: string; role: string; is_active: boolean; password: string }>) =>
    request<User>(`/api/users/${id}`, { method: 'PATCH', json: body }),

  projects: (includeArchived = false) => request<Project[]>(`/api/projects${qs({ include_archived: includeArchived ? 'true' : null })}`),
  project: (id: number) => request<Project>(`/api/projects/${id}`),
  createProject: (body: ProjectInput) => request<Project>('/api/projects', { json: body }),
  updateProject: (id: number, body: Partial<ProjectInput> & { review_enabled?: boolean; archived?: boolean }) =>
    request<Project>(`/api/projects/${id}`, { method: 'PATCH', json: body }),
  regenerateLink: (id: number) => request<Project>(`/api/projects/${id}/review-link/regenerate`, { method: 'POST' }),
  pages: (id: number) => request<Page[]>(`/api/projects/${id}/pages`),
  addPage: (id: number, url: string) => request<Page>(`/api/projects/${id}/pages`, { json: { url } }),
  deletePage: (id: number, pageId: number) => request<void>(`/api/projects/${id}/pages/${pageId}`, { method: 'DELETE' }),
  capture: (id: number, pageId: number, viewport: Viewport) =>
    request<SnapshotSummary>(`/api/projects/${id}/pages/${pageId}/capture`, { json: { viewport } }),
  snapshot: (id: number) => request<Snapshot>(`/api/snapshots/${id}`),

  changeRequests: (params: { status?: RequestStatus | null; project_id?: number | null; assignee_id?: number | null }) =>
    request<ChangeRequestList>(`/api/change-requests${qs(params)}`),
  changeRequest: (id: number) => request<ChangeRequestDetail>(`/api/change-requests/${id}`),
  updateChangeRequest: (id: number, body: TaskUpdate) =>
    request<ChangeRequestDetail>(`/api/change-requests/${id}`, { method: 'PATCH', json: body }),
  setStatus: (id: number, status: RequestStatus, message?: string) =>
    request<ChangeRequestDetail>(`/api/change-requests/${id}/status`, { json: { status, message } }),
  clarify: (id: number, question: string) =>
    request<ChangeRequestDetail>(`/api/change-requests/${id}/clarify`, { json: { question } }),
  addMessage: (id: number, body: string, visibility: 'client' | 'internal') =>
    request<ChangeRequestDetail>(`/api/change-requests/${id}/messages`, { json: { body, visibility } }),
  regenerate: (id: number) => request<ChangeRequestDetail>(`/api/change-requests/${id}/regenerate`, { method: 'POST' }),
  exportMarkdown: (id: number) => request<string>(`/api/change-requests/${id}/export.md`),
  publish: (id: number, key: string) =>
    request<ChangeRequestDetail>(`/api/change-requests/${id}/integrations/${key}`, { method: 'POST' }),
}
